"use client";

import { useQuery } from "convex/react";
import Link from "next/link";
import { Loader2 } from "lucide-react";
import { api } from "../../../../../../convex/_generated/api";
import type { Doc } from "../../../../../../convex/_generated/dataModel";
import { resolveEventAdmissionMode } from "../../../../../../convex/lib/eventTicketing";
import { getLocationBySlug } from "@/lib/restaurant-platform";
import { Button } from "@/components/ui/button";
import { TicketCheckoutClient } from "./ticket-checkout-client";

type PublishedTicketEvent = Doc<"events">;

function isFirstPartyCheckoutEvent(
  event: PublishedTicketEvent | null | undefined,
): event is PublishedTicketEvent {
  return Boolean(
    event &&
      event.locationId &&
      event.status === "published" &&
      resolveEventAdmissionMode(event) === "first_party_ticket",
  );
}

function venueNameForEvent(
  event: PublishedTicketEvent,
  fallbackVenueName: string,
): string {
  try {
    return getLocationBySlug(event.locationId as string).name;
  } catch {
    return fallbackVenueName;
  }
}

export function TicketCheckoutGate({
  slug,
  locationId,
  fallbackVenueName,
  initialEvent,
}: {
  slug: string;
  locationId: string;
  fallbackVenueName: string;
  initialEvent: PublishedTicketEvent | null;
}) {
  const liveEvent = useQuery(api.events.getBySlug, { slug, locationId });
  const event = liveEvent !== undefined ? liveEvent : initialEvent;

  if (liveEvent === undefined && initialEvent === null) {
    return (
      <main
        className="flex min-h-screen items-center justify-center bg-background px-4 text-foreground"
        aria-busy="true"
        aria-label="Loading ticket checkout"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading tickets…
        </span>
      </main>
    );
  }

  if (!isFirstPartyCheckoutEvent(event)) {
    return (
      <main className="min-h-screen bg-background px-4 py-16 text-foreground">
        <div className="mx-auto max-w-md text-center">
          <h1 className="font-serif text-2xl">Tickets are not on sale</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This event is not offering first-party ticket checkout.
          </p>
          <Button asChild className="mt-6 rounded-full">
            <Link href={`/events/${slug}`}>Back to event</Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <TicketCheckoutClient
      ticketEvent={{
        id: event._id,
        slug: event.slug ?? slug,
        title: event.title,
        date: event.date,
        startTime: event.startTime,
        venueName: venueNameForEvent(event, fallbackVenueName),
        unitPriceCents: event.ticketPriceCents ?? 0,
        menuHighlight: event.menuHighlight,
        salesPaused: event.ticketSalesPaused === true,
      }}
    />
  );
}
